import { DiagnosticsPage } from '@/app/pages/Diagnostics/DiagnosticsPage'
import {
	ActionSettingsPage,
	AnomaliesPage,
	ApiKeysSettingsPage,
	AuditLogsPage,
	DeviceStatePage,
	MetadataPage,
	ProjectGeneralPage,
	ProjectModulesPage,
	QuotasPage,
	RoleSettingsPage,
	SettingsPage,
	StreamsPage,
	SubscriberGroupsPage,
	UsersSettingsPage,
} from '@/app/routes/lazyPages'
import { apiTypeSchema, subscriberGroupTypeSchema, tabAllOnlySchema, tabAnySchema } from '@/app/routes/params'
import { organizationProjectRoute } from '@/app/routes/projectRoutes'
import { redirectToAllTab } from '@/app/routes/redirectToAllTab'
import { RouteAccessBoundary } from '@/components/access-control'
import { authenticatedRouteGuard, projectSettingsAdminRouteGuard } from '@/lib/routeGuards'
import { anomaliesSearchSchema, streamsSearchSchema } from '@/lib/searchSchemas'
import { Outlet, createRoute, redirect } from '@tanstack/react-router'

/**
 * The project settings subtree, `/orgs/$orgId/projects/$tenantId/settings`.
 *
 * Admin gating is per tab rather than on the parent: general, users, roles, quotas, audit logs and API
 * keys are admin only, while metadata, streams, action settings and subscriber groups stay open to
 * every member and restrict their own bodies through RBAC. Modules is open to every member and turns its
 * switches read only for anyone who is not an admin. Anomalies is open to every member outright: its
 * `anomalies` resource declares no permissions, so every role can view it.
 */
export const settingsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/settings',
	component: () => <SettingsPage />,
	// Admin gating is per-tab below (general/users/roles/quotas/audit-logs/api-keys); the
	// RBAC-gated tabs (metadata/streams/action-settings/subscriber-groups) stay open to non-admins.
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// Index route for settings that redirects to general
export const settingsIndexRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/general',
			params,
			search,
			replace: true,
		})
	},
})

export const generalSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/general',
	component: () => (
		<RouteAccessBoundary resource='project_settings'>
			<ProjectGeneralPage />
		</RouteAccessBoundary>
	),
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

export const modulesSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/modules',
	component: () => <ProjectModulesPage />,
})

export const ApiKeysSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/api-keys',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
export const settingsApiKeysIndexRoute = createRoute({
	getParentRoute: () => ApiKeysSettingsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/api-keys/$type'),
})
export const settingsApiKeysTypeRoute = createRoute({
	getParentRoute: () => ApiKeysSettingsRoute,
	path: '/$type',
	parseParams: params => apiTypeSchema.parse(params),
	component: () => <ApiKeysSettingsPage />,
})

export const UsersSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/users',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
export const settingsUsersIndexRoute = createRoute({
	getParentRoute: () => UsersSettingsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/users/$type'),
})
// Add users with type parameter:
export const settingsUsersTypeRoute = createRoute({
	getParentRoute: () => UsersSettingsRoute,
	path: '/$type',
	parseParams: params => tabAnySchema.parse(params),
	component: () => <UsersSettingsPage />,
})

export const RoleSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/roles',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
export const settingsRoleIndexRoute = createRoute({
	getParentRoute: () => RoleSettingsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/roles/$type'),
})
export const settingsRoleTypeRoute = createRoute({
	getParentRoute: () => RoleSettingsRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <RoleSettingsPage />,
})

export const QuotasSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/quotas',
	component: () => (
		<RouteAccessBoundary resource='project_settings'>
			<QuotasPage />
		</RouteAccessBoundary>
	),
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

// SETTINGS — Metadata (moved from Data section)
export const settingsMetadataRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/metadata',
	// No route-level boundary: MetadataPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See MetadataPage.
	component: () => <Outlet />,
})
export const settingsMetadataIndexRoute = createRoute({
	getParentRoute: () => settingsMetadataRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/metadata/$type'),
})
export const settingsMetadataTypeRoute = createRoute({
	getParentRoute: () => settingsMetadataRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <MetadataPage />,
})

// No boundary at route level: DeviceStatePage keeps its header mounted and restricts its own body.
export const settingsDeviceStateRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/device-state',
	component: () => <Outlet />,
})
export const settingsDeviceStateIndexRoute = createRoute({
	getParentRoute: () => settingsDeviceStateRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/device-state/$type'),
})
export const settingsDeviceStateTypeRoute = createRoute({
	getParentRoute: () => settingsDeviceStateRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <DeviceStatePage />,
})

// SETTINGS — Streams (moved from Data section)
export const settingsStreamsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/streams',
	// No route-level boundary: StreamsPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See StreamsPage.
	component: () => <Outlet />,
})
export const settingsStreamsIndexRoute = createRoute({
	getParentRoute: () => settingsStreamsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/streams/$type'),
})
export const settingsStreamsTypeRoute = createRoute({
	getParentRoute: () => settingsStreamsRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	validateSearch: streamsSearchSchema,
	component: () => <StreamsPage />,
})

// SETTINGS — Action Settings (moved from Actions section)
export const settingsActionSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/action-settings',
	// No route-level boundary: ActionSettingsPage stays mounted so its header is kept, and the body
	// self-restricts via per-resource RBAC. See ActionSettingsPage.
	component: () => <Outlet />,
})
export const settingsActionSettingsIndexRoute = createRoute({
	getParentRoute: () => settingsActionSettingsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/action-settings/$type'),
})
export const settingsActionSettingsTypeRoute = createRoute({
	getParentRoute: () => settingsActionSettingsRoute,
	path: '/$type',
	// Bookmarks to the retired active and inactive tabs land on all instead of a 404.
	parseParams: params => apiTypeSchema.parse(params),
	component: () => <ActionSettingsPage />,
})

// SETTINGS — Subscriber Groups (moved from Sessions section)
export const settingsSubscriberGroupsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/subscriber-groups',
	// No route-level boundary: SubscriberGroupsPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See SubscriberGroupsPage.
	component: () => <Outlet />,
})
export const settingsSubscriberGroupsIndexRoute = createRoute({
	getParentRoute: () => settingsSubscriberGroupsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/subscriber-groups/$type'),
})
export const settingsSubscriberGroupsTypeRoute = createRoute({
	getParentRoute: () => settingsSubscriberGroupsRoute,
	path: '/$type',
	parseParams: params => subscriberGroupTypeSchema.parse(params),
	component: () => <SubscriberGroupsPage />,
})

export const diagnosticsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/diagnostics',
	component: () => <DiagnosticsPage />,
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// SETTINGS — Anomalies. Open to every role: no projectSettingsAdminRouteGuard, and the `anomalies`
// resource in access-control.ts declares no permissions, so it can't appear in Create/Edit Role either.
export const anomaliesRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/anomalies',
	validateSearch: anomaliesSearchSchema,
	component: () => <AnomaliesPage />,
})

export const auditLogsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/audit-logs',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

// Index route for audit logs that redirects to all
export const auditLogsIndexRoute = createRoute({
	getParentRoute: () => auditLogsRoute,
	path: '/',
	beforeLoad: redirectToAllTab('/orgs/$orgId/projects/$tenantId/settings/audit-logs/$type'),
})

// Audit logs with type parameter
export const auditLogsTypeRoute = createRoute({
	getParentRoute: () => auditLogsRoute,
	path: '/$type',
	parseParams: params => tabAllOnlySchema.parse(params),
	component: () => <AuditLogsPage />,
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })
	},
})

/** The settings subtree, assembled so the composition root nests sections rather than leaf routes. */
export const projectSettingsRouteTree = settingsRoute.addChildren([
	settingsIndexRoute,
	generalSettingsRoute,
	modulesSettingsRoute,
	UsersSettingsRoute.addChildren([settingsUsersIndexRoute, settingsUsersTypeRoute]),
	RoleSettingsRoute.addChildren([settingsRoleIndexRoute, settingsRoleTypeRoute]),
	ApiKeysSettingsRoute.addChildren([settingsApiKeysIndexRoute, settingsApiKeysTypeRoute]),
	QuotasSettingsRoute,
	diagnosticsRoute,
	settingsMetadataRoute.addChildren([settingsMetadataIndexRoute, settingsMetadataTypeRoute]),
	settingsDeviceStateRoute.addChildren([settingsDeviceStateIndexRoute, settingsDeviceStateTypeRoute]),
	settingsStreamsRoute.addChildren([settingsStreamsIndexRoute, settingsStreamsTypeRoute]),
	settingsActionSettingsRoute.addChildren([settingsActionSettingsIndexRoute, settingsActionSettingsTypeRoute]),
	settingsSubscriberGroupsRoute.addChildren([settingsSubscriberGroupsIndexRoute, settingsSubscriberGroupsTypeRoute]),
	anomaliesRoute,
	auditLogsRoute.addChildren([auditLogsIndexRoute, auditLogsTypeRoute]),
])
